import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { JsonService } from '../../_services/json.service';
import { LoggerService } from '../../_services/logger.service';
import { Country } from '../../_models/country.model';
import { Group } from '../../_models';
import { ResumoBatchPdv } from '../../_models/resumo-batch-pdv.model';

@Component({
  selector: 'app-resumo-batch-pdv',
  templateUrl: './resumo-batch-pdv.component.html',
  styleUrls: ['./resumo-batch-pdv.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResumoBatchPdvComponent implements OnInit {

  filtro = { countryCode: '', ano: '', mes: '', cdgCiu: '', cdgFcia: '', groupId: '' };

  listPaises: Country[] = [];
  listGrupos: Group[] = [];

  readonly listMeses = [
    { label: 'Janeiro',   value: '01_ENERO' },
    { label: 'Fevereiro', value: '02_FEBRERO' },
    { label: 'Março',     value: '03_MARZO' },
    { label: 'Abril',     value: '04_ABRIL' },
    { label: 'Maio',      value: '05_MAYO' },
    { label: 'Junho',     value: '06_JUNIO' },
    { label: 'Julho',     value: '07_JULIO' },
    { label: 'Agosto',    value: '08_AGOSTO' },
    { label: 'Setembro',  value: '09_SEPTIEMBRE' },
    { label: 'Outubro',   value: '10_OCTUBRE' },
    { label: 'Novembro',  value: '11_NOVIEMBRE' },
    { label: 'Dezembro',  value: '12_DICIEMBRE' },
  ];

  readonly listAnos: string[] = (() => {
    const base = new Date().getFullYear();
    return [base - 1, base, base + 1].map(y => String(y));
  })();

  dados: ResumoBatchPdv[] = [];
  isLoading = false;
  semDados  = false;
  filtroGrupoAplicado = false;

  totais = this.totaisZerados();

  constructor(
    private jsonService: JsonService,
    private logger: LoggerService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const now = new Date();
    this.filtro.ano = String(now.getFullYear());
    this.filtro.mes = this.listMeses[now.getMonth()].value;
    this.jsonService.listCountry().subscribe({
      next: paises => { this.listPaises = paises || []; this.cdr.markForCheck(); },
      error: erro  => { this.logger.error('Erro ao carregar países', erro); }
    });
  }

  onPaisChange(): void {
    this.filtro.groupId = '';
    if (!this.filtro.countryCode) {
      this.listGrupos = [];
      this.cdr.markForCheck();
      return;
    }
    this.jsonService.listGroupsByCountry(this.filtro.countryCode).subscribe({
      next: grupos => { this.listGrupos = grupos || []; this.cdr.markForCheck(); },
      error: erro  => { this.logger.error('Erro ao carregar grupos', erro); this.listGrupos = []; this.cdr.markForCheck(); }
    });
  }

  buscar(): void {
    if (!this.filtro.countryCode || !this.filtro.ano || !this.filtro.mes) return;

    this.isLoading = true;
    this.semDados  = false;
    this.filtroGrupoAplicado = !!this.filtro.groupId;
    this.cdr.markForCheck();

    let params = new HttpParams()
      .set('countryCode', this.filtro.countryCode)
      .set('ano', this.filtro.ano)
      .set('mes', this.filtro.mes);

    if (this.filtro.cdgCiu.trim())   params = params.set('cdgCiu',  this.filtro.cdgCiu.trim());
    if (this.filtro.cdgFcia.trim())  params = params.set('cdgFcia', this.filtro.cdgFcia.trim());
    if (this.filtro.groupId)         params = params.set('groupId', this.filtro.groupId);

    this.jsonService.getDashboardResumoBatchPdv(params).subscribe({
      next: data => {
        this.dados    = (data as ResumoBatchPdv[]) || [];
        this.semDados = this.dados.length === 0;
        this.calcularTotais();
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: erro => {
        this.logger.error('Erro ao carregar Resumo Batch por PDV', erro);
        this.semDados  = true;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  limpar(): void {
    const now = new Date();
    this.filtro   = { countryCode: '', ano: String(now.getFullYear()), mes: this.listMeses[now.getMonth()].value, cdgCiu: '', cdgFcia: '', groupId: '' };
    this.listGrupos = [];
    this.dados    = [];
    this.semDados = false;
    this.filtroGrupoAplicado = false;
    this.totais   = this.totaisZerados();
    this.cdr.markForCheck();
  }

  // ── trackBy ──────────────────────────────────────────────────────────

  trackByRow(_: number, row: ResumoBatchPdv): string {
    return row.fechaProceso + row.cdgCiu + row.cdgFcia;
  }

  trackByCountryCode(_: number, item: Country): string {
    return item.countryCode;
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByGroupId(_: number, item: Group): string {
    return item.id;
  }

  // ── Exportação CSV ─────────────────────────────────────────────────

  exportarCsv(): void {
    const headers = [
      'FechaProceso', 'CdgCiu', 'CdgFcia', 'DescFcia',
      'Total Rxs Imgs', 'No son Rxs', 'Imgs en Cola', 'Procesadas',
      'Validadas APPI', 'Validadas DE', 'Pend. Revisión',
      '% APPI', '% DE', '% Pend. Rev.', 'Grupo'
    ];

    const linhas = this.dados.map(r => [
      r.fechaProceso, r.cdgCiu, r.cdgFcia, r.descFcia,
      r.totalImagenes, r.noSonRxs, r.imagenesEnCola, r.imagenesProcesadas,
      r.validasAPPI, r.validasDE, r.pendientesRevision,
      (r.imagenesProcesadas > 0 ? (r.validasAPPI / r.imagenesProcesadas * 100) : 0).toFixed(2),
      (r.imagenesProcesadas > 0 ? (r.validasDE / r.imagenesProcesadas * 100) : 0).toFixed(2),
      (r.imagenesProcesadas > 0 ? (r.pendientesRevision / r.imagenesProcesadas * 100) : 0).toFixed(2),
      r.grupo
    ].join(';'));

    const csv = [headers.join(';'), ...linhas].join('\n');
    const aaaamm = `${this.filtro.ano}${this.filtro.mes.substring(0, 2)}`;
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url  = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const ts = new Date(); const stamp = `${ts.getFullYear()}${String(ts.getMonth()+1).padStart(2,'0')}${String(ts.getDate()).padStart(2,'0')}${String(ts.getHours()).padStart(2,'0')}${String(ts.getMinutes()).padStart(2,'0')}${String(ts.getSeconds()).padStart(2,'0')}`;
    link.setAttribute('download', `${aaaamm}–${this.filtro.countryCode}–pdv_${stamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // ── Internos ─────────────────────────────────────────────────────────

  private calcularTotais(): void {
    const sum = (campo: keyof ResumoBatchPdv) =>
      this.dados.reduce((acc, r) => acc + (Number(r[campo]) || 0), 0);

    const imgProc = sum('imagenesProcesadas');

    this.totais = {
      totalImagenes:      sum('totalImagenes'),
      noSonRxs:           sum('noSonRxs'),
      imagenesEnCola:     sum('imagenesEnCola'),
      imagenesProcesadas: imgProc,
      validasAPPI:        sum('validasAPPI'),
      validasDE:          sum('validasDE'),
      pendientesRevision: sum('pendientesRevision'),
      porcValidasAPPI:    imgProc > 0 ? sum('validasAPPI')        / imgProc * 100 : 0,
      porcValidasDE:      imgProc > 0 ? sum('validasDE')          / imgProc * 100 : 0,
      porcPendientes:     imgProc > 0 ? sum('pendientesRevision') / imgProc * 100 : 0,
    };
  }

  private totaisZerados() {
    return {
      totalImagenes: 0, noSonRxs: 0, imagenesEnCola: 0, imagenesProcesadas: 0,
      validasAPPI: 0, validasDE: 0, pendientesRevision: 0,
      porcValidasAPPI: 0, porcValidasDE: 0, porcPendientes: 0,
    };
  }
}
